import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { GET_QUOTATIONS } from '../../apollo/queries/quotationQueries';
import '../css/CustomerQuotationPage.css';
import ImageWithPlaceholder from '../../components/ImageWithPlaceholder';

const CustomerQuotationPage = () => {
    const [searchParams] = useSearchParams();
    const userId = searchParams.get('id');
    const navigate = useNavigate();

    // 검색 필터 상태
    const [searchFilters, setSearchFilters] = useState({
        quotationNumber: '',
        startDate: '',
        endDate: '',
        productName: '',
        status: 'all'
    });

    // GraphQL 쿼리 - 모든 견적을 가져와서 클라이언트에서 필터링
    const { data, loading, error, refetch } = useQuery(GET_QUOTATIONS, {
        errorPolicy: 'all'
    });

    const handleQuotationClick = (quoteId) => {
        navigate(`/admin/customer/quotation-detail?id=${quoteId}`);
    };

    const handleFilterChange = (field, value) => {
        setSearchFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // 날짜 단축키 핸들러
    const handleDateShortcut = (months) => {
        const today = new Date();
        const pastDate = new Date(today);
        
        if (months === 'all') {
            setSearchFilters(prev => ({
                ...prev,
                startDate: '',
                endDate: ''
            }));
        } else {
            pastDate.setMonth(pastDate.getMonth() - months);
            setSearchFilters(prev => ({
                ...prev,
                startDate: pastDate.toISOString().split('T')[0],
                endDate: today.toISOString().split('T')[0]
            }));
        }
    };

    // 이미지 파싱 함수
    const parseProductImage = (images) => {
        if (!images) return null;
        
        try {
            if (typeof images === 'string') {
                const cleanedImages = images.trim();
                if (cleanedImages && cleanedImages !== '[]' && cleanedImages !== '') {
                    if (cleanedImages.startsWith('[')) {
                        const parsedImages = JSON.parse(cleanedImages);
                        if (Array.isArray(parsedImages) && parsedImages.length > 0) {
                            return parsedImages[0];
                        }
                    } else if (cleanedImages.includes('http') || cleanedImages.includes('/')) {
                        return cleanedImages;
                    }
                }
            } else if (Array.isArray(images) && images.length > 0) {
                return images[0];
            }
        } catch (error) {
            console.warn('이미지 파싱 오류:', error);
        }
        
        return null;
    };

    // 견적 데이터 필터링
    const getFilteredQuotations = () => {
        if (!data?.quotations) return [];
        
        // 먼저 해당 사용자의 견적만 필터링
        let filtered = data.quotations.filter(quotation => 
            quotation.user.id === parseInt(userId)
        );

        // 견적번호 필터
        if (searchFilters.quotationNumber.trim()) {
            const searchValue = searchFilters.quotationNumber.toLowerCase();
            filtered = filtered.filter(quotation => 
                quotation.id.toString().includes(searchValue)
            );
        }

        // 날짜 필터
        if (searchFilters.startDate || searchFilters.endDate) {
            const startDate = searchFilters.startDate ? new Date(searchFilters.startDate) : new Date('1900-01-01');
            const endDate = searchFilters.endDate ? new Date(searchFilters.endDate) : new Date('2100-12-31');
            endDate.setHours(23, 59, 59, 999);
            
            filtered = filtered.filter(quotation => {
                const quotationDate = new Date(quotation.createdAt);
                return quotationDate >= startDate && quotationDate <= endDate;
            });
        }

        // 제품명 필터
        if (searchFilters.productName.trim()) {
            const searchValue = searchFilters.productName.toLowerCase();
            filtered = filtered.filter(quotation => 
                quotation.quotationItems?.some(item => 
                    item.product?.name?.toLowerCase().includes(searchValue)
                )
            );
        }

        // 상태 필터
        if (searchFilters.status !== 'all') {
            filtered = filtered.filter(quotation => quotation.status === searchFilters.status);
        }

        return filtered;
    };

    const filteredQuotations = getFilteredQuotations();

    // 상태 표시 함수
    const getStatusText = (status) => {
        switch (status) {
            case 'PENDING': return '대기중';
            case 'COMPLETED': return '견적완료';
            case 'REJECTED': return '거절됨';
            default: return status;
        }
    };

    // userId가 없는 경우
    if (!userId) {
        return (
            <div className="customer-quotation-container">
                <div style={{ textAlign: 'center', padding: '50px', color: '#ff4444' }}>
                    <div>사용자 ID가 제공되지 않았습니다.</div>
                    <button 
                        onClick={() => navigate('/admin/customer')}
                        style={{ marginTop: '20px', padding: '10px 20px', cursor: 'pointer' }}
                    >
                        고객 목록으로 돌아가기
                    </button>
                </div>
            </div>
        );
    }

    // 로딩 상태
    if (loading) {
        return (
            <div className="customer-quotation-container">
                <div style={{ textAlign: 'center', padding: '50px' }}>
                    <div>견적 내역을 불러오는 중입니다...</div>
                </div>
            </div>
        );
    }

    // 에러 상태
    if (error) {
        return (
            <div className="customer-quotation-container">
                <div style={{ textAlign: 'center', padding: '50px', color: '#ff4444' }}>
                    <div>견적 내역을 불러오는 중 오류가 발생했습니다: {error.message}</div>
                    <button 
                        onClick={() => refetch()}
                        style={{ marginTop: '20px', padding: '10px 20px', cursor: 'pointer' }}
                    >
                        다시 시도
                    </button>
                </div>
            </div>
        );
    }

    return (
            <div className="customer-quotation-container">
                <div className="breadcrumb">
                    <span>홈 &gt; 회원 정보 관리 &gt; 고객 #{userId} &gt; 견적내역</span>
                </div>

                <div className="page-header">
                    <div className="page-title-bar"></div>
                    <h2>견적 내역</h2>
                </div>

                <div className="search-section">
                    <div className="search-row">
                        <label>견적 번호</label>
                        <input 
                            type="text" 
                            placeholder="견적 번호를 입력해 주세요."
                            value={searchFilters.quotationNumber}
                            onChange={(e) => handleFilterChange('quotationNumber', e.target.value)}
                        />
                    </div>
                    <div className="search-row">
                        <label>견적일</label>
                        <input 
                            type="date" 
                            className="date-input"
                            value={searchFilters.startDate}
                            onChange={(e) => handleFilterChange('startDate', e.target.value)}
                        />
                        <span>~</span>
                        <input 
                            type="date" 
                            className="date-input"
                            value={searchFilters.endDate}
                            onChange={(e) => handleFilterChange('endDate', e.target.value)}
                        />
                        <div className="date-buttons">
                            <button 
                                className={!searchFilters.startDate && !searchFilters.endDate ? 'active' : ''}
                                onClick={() => handleDateShortcut(1)}
                            >
                                1개월
                            </button>
                            <button 
                                onClick={() => handleDateShortcut(3)}
                            >
                                3개월
                            </button>
                            <button 
                                onClick={() => handleDateShortcut('all')}
                            >
                                전체
                            </button>
                        </div>
                    </div>
                    <div className="search-row">
                        <label>제품 명</label>
                        <input 
                            type="text" 
                            placeholder="제품명을 입력해주세요."
                            value={searchFilters.productName}
                            onChange={(e) => handleFilterChange('productName', e.target.value)}
                        />
                    </div>
                    <div className="search-row">
                        <label>견적 상황</label>
                        <div className="status-buttons">
                            <button 
                                className={searchFilters.status === 'all' ? 'active' : ''}
                                onClick={() => handleFilterChange('status', 'all')}
                            >
                                전체
                            </button>
                            <button 
                                className={searchFilters.status === 'PENDING' ? 'active' : ''}
                                onClick={() => handleFilterChange('status', 'PENDING')}
                            >
                                대기 중
                            </button>
                            <button 
                                className={searchFilters.status === 'COMPLETED' ? 'active' : ''}
                                onClick={() => handleFilterChange('status', 'COMPLETED')}
                            >
                                견적 완료
                            </button>
                            <button 
                                className={searchFilters.status === 'REJECTED' ? 'active' : ''}
                                onClick={() => handleFilterChange('status', 'REJECTED')}
                            >
                                거절됨
                            </button>
                        </div>
                    </div>
                </div>

                <div className="search-actions">
                    <button 
                        className="btn-search"
                        onClick={() => refetch()}
                    >
                        검색
                    </button>
                    <button 
                        className="btn-reset"
                        onClick={() => setSearchFilters({
                            quotationNumber: '',
                            startDate: '',
                            endDate: '',
                            productName: '',
                            status: 'all'
                        })}
                    >
                        검색 결과 해제
                    </button>
                </div>

                <div className="quotation-list-container">
                    <div className="list-header">
                        <span>총 견적 수 : {filteredQuotations.length} 건</span>
                    </div>
                    <table className="quotation-table">
                        <thead>
                            <tr>
                                <th>NO</th>
                                <th>견적번호</th>
                                <th>제품 명</th>
                                <th>상태</th>
                                <th>메일 발송 여부</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredQuotations.length === 0 ? (
                                <tr>
                                    <td colSpan="5" style={{ textAlign: 'center', padding: '50px' }}>
                                        등록된 견적이 없습니다.
                                    </td>
                                </tr>
                            ) : (
                                filteredQuotations.map((quotation, index) => {
                                    // 제품명 생성 (첫 번째 제품명 + 외 n건)
                                    const firstProduct = quotation.quotationItems[0]?.product;
                                    const additionalCount = quotation.quotationItems.length - 1;
                                    const productName = firstProduct 
                                        ? `${firstProduct.name}${additionalCount > 0 ? ` 외 ${additionalCount}건` : ''}`
                                        : '제품 정보 없음';
                                    
                                    // 첫 번째 제품 이미지
                                    const productImage = firstProduct ? parseProductImage(firstProduct.images) : null;
                                    
                                    return (
                                        <tr key={quotation.id} onClick={() => handleQuotationClick(quotation.id)} style={{ cursor: 'pointer' }}>
                                            <td>{index + 1}</td>
                                            <td>
                                                {quotation.id} (견적일: {new Date(quotation.createdAt).toLocaleDateString('ko-KR')})
                                            </td>
                                            <td>
                                                <div className="product-info-cell">
                                                    <ImageWithPlaceholder 
                                                        src={productImage} 
                                                        alt="product"
                                                        className="admin-quotation-image"
                                                    />
                                                    <div className="product-text-info">
                                                        <span>{productName}</span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>{getStatusText(quotation.status)}</td>
                                            <td>
                                                {quotation.status === 'COMPLETED' ? '메일발송 완료' : '대기중'}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="page-actions">
                    <button className="btn-secondary" onClick={() => navigate(-1)}>
                        <span className="arrow-icon">&lt;</span> 돌아가기
                    </button>
                </div>
            </div>
    );
};

export default CustomerQuotationPage; 